export const privacyPolicyMarkdown = `
# Privacy Policy

**Effective as of:** {{EFFECTIVE_DATE}}  
**Website:** {{WEBSITE_URL}}  
**Contact:** {{SUPPORT_EMAIL}}

## IMPORTANT PRIVACY INFORMATION

When you access our Website, we may automatically collect language settings, IP address, time zone, device type/model, device settings, operating system, internet provider, mobile carrier, hardware ID, advertising identifiers, and cookies.

We use third-party tools and vendors to provide the Service, analytics, communications, ads, and payments, including: Amazon, Amplitude, Appsflyer, BigQuery, Crashlytics, Firebase, Google, Hotjar, Iterable, Meta, OpenAI, PayPal, Solidgate, Tableau, TikTok, Zendesk.

By using the Service, you confirm that:
1. You have read and agreed to this Privacy Policy.
2. You are at least 16 years old (or your parent/legal guardian has agreed on your behalf).

If you do not agree, do not use the Service and contact us at {{SUPPORT_EMAIL}}.

---

## 1. Personal Data Controller

The data controller is: **{{COMPANY_LEGAL_NAME}}**, contact email: **{{SUPPORT_EMAIL}}**.

---

## 2. Categories of Personal Data We Collect

### 2.1 Data you provide
- Name
- Email
- Account details
- Input/files/feedback submitted to the Service
- Payment-related records required for order processing

### 2.2 Data collected automatically
- Referral source (where you came from)
- Device and technical data (IP, OS, browser, language, timezone, identifiers)
- Usage data (pages viewed, interactions, session behavior, purchases)
- Advertising identifiers (IDFA/AAID/IDFV where applicable)
- Transaction metadata (date/time/amount/payment method type)
- Cookies and similar technologies

---

## 3. Purposes of Processing

We process personal data to:

1. Provide and operate the Service
2. Generate requested outputs
3. Deliver customer support
4. Communicate service-related notices
5. Analyze and improve the Service
6. Send marketing communications
7. Personalize and measure advertising
8. Process payments
9. Enforce Terms and prevent fraud
10. Comply with legal obligations

Third-party processors may include:
- Hosting/infrastructure: Amazon Web Services, Google Cloud
- AI services: OpenAI
- Analytics: Amplitude, Appsflyer, Firebase, BigQuery, Tableau, Hotjar, Meta
- Communications/support: Zendesk, Firebase, Iterable
- Advertising: Meta, Google, TikTok
- Payments: Solidgate, PayPal

---

## 4. Legal Bases (EEA Users)

For EEA users, processing is based on:
1. Performance of a contract
2. Legitimate interests (service improvement, communications, fraud prevention, marketing, ad personalization)
3. Compliance with legal obligations

---

## 5. Data Sharing

We may share data with:
1. Service providers and processors supporting our operations
2. Authorities/law enforcement when legally required
3. Parties involved in corporate transactions (merger, acquisition, restructuring)

---

## 6. Your Privacy Rights

Depending on jurisdiction, you may have rights to:
- Access, review, correct your data
- Delete your data
- Restrict or object to processing
- Data portability
- Lodge a complaint with a supervisory authority (EEA)

To exercise rights, contact: **{{SUPPORT_EMAIL}}**.

---

## 7. Age Limitation

We do not knowingly process personal data of users under 16.

---

## 8. International Data Transfers

Your data may be transferred across jurisdictions to provide the Service.  
Where required, we use safeguards (e.g., SCCs, adequacy decisions).

---

## 9. Changes to This Privacy Policy

We may update this Policy from time to time. Material changes will be communicated via appropriate channels (e.g., email or in-product notice).

---

## 10. California Privacy Rights

California residents may have rights to:
- Know what personal information is collected/used/shared
- Request deletion
- Opt out where applicable
- Non-discrimination for exercising rights

Requests: **{{SUPPORT_EMAIL}}**.

---

## 11. Data Retention

We retain data as long as reasonably necessary for Service purposes, legal compliance, dispute resolution, and enforcement.

---

## 12. “Do Not Track”

Except where explicitly stated, the Website does not respond to “Do Not Track” signals.

---

## 13. Contact Us

For any privacy questions or requests: **{{SUPPORT_EMAIL}}**.
`;

export const subscriptionTermsMarkdown = `
# Subscription Terms

**Last Updated:** {{LAST_UPDATED}}  
**Service:** {{SITE_NAME}}  
**Contact:** {{SUPPORT_EMAIL}}

## 1. Trial

We may offer free or paid trial access.  
Unless canceled at least 24 hours before trial end, trial may auto-convert to a paid subscription at the price and period shown at checkout.

## 2. Subscription

Subscriptions renew automatically at the end of each billing cycle (weekly/monthly/6 months/year/other selected period) until canceled.

## 3. Payment Method

Charges are billed to the payment method provided at purchase.  
You authorize us to charge applicable subscription fees.

## 4. Cancellation

Canceling disables future auto-renewal, while access remains until the current paid period ends.  
You can cancel via account settings or by contacting support: **{{SUPPORT_EMAIL}}**.

If cancellation is linked to a personal-data-erasure request, access may terminate immediately after such request is completed.

## 5. Changes

To the extent permitted by law, we may change subscription fees at any time with reasonable notice (in-app, email, or other prominent notice).  
If you do not accept new fees, cancel before they take effect.

## 6. Refund Rules

### 6.1 General
Purchases are generally non-refundable/non-exchangeable unless required by law or explicitly stated otherwise.

### 6.2 EU Users
EU users may withdraw within 14 days without reason.  
To withdraw, contact us by email at **{{SUPPORT_EMAIL}}** (withdrawal form may be offered but is not mandatory).  
If withdrawal is valid, reimbursement is made within 14 days via original payment method (unless otherwise agreed).

If you expressly consented to immediate digital-service performance and acknowledged withdrawal-right loss, refunds may be limited as required by applicable law.

---

If you have questions about these Subscription Terms, contact: **{{SUPPORT_EMAIL}}**.
`;

export const termsOfUseMarkdown = `
# Terms and Conditions of Use

**Last Updated:** {{LAST_UPDATED}}  
**Website:** {{WEBSITE_URL}}  
**Contact:** {{SUPPORT_EMAIL}}

> The Service may offer subscriptions that automatically renew.  
> To avoid charges, cancel trial/subscription at least 24 hours before renewal.

---

## 1. Acceptance of Terms

1. These Terms govern your use of {{WEBSITE_URL}} and related services/content.
2. By using the Service, you agree to be legally bound by these Terms.
3. Privacy Policy is incorporated by reference.
4. English version controls in case of translation conflicts.
5. We may update Terms; continued use means acceptance.
6. Terms include important disclaimers, limitation of liability, arbitration, and class action waiver.
7. If you do not agree, do not use the Service.

---

## 2. Important Disclaimers

We do not guarantee uninterrupted, error-free, fully secure Service, or that outcomes will meet your expectations.

---

## 3. Account Registration

- You may need an account for certain features.
- You must provide accurate/current information.
- Minimum age is 16 (or parental/guardian consent where required).
- We may suspend/terminate for violations.
- You are responsible for account credentials and activity.

---

## 4. Service Conditions

- Service may be modified/suspended at any time.
- You are responsible for required equipment/internet access.
- Access/use is at your own risk.
- Support may be provided at our discretion.

---

## 5. Intellectual Property and User Content

1. We grant a limited, non-exclusive, revocable license to use the Service.
2. Service IP remains owned by us/licensors.
3. Unauthorized copying, reverse engineering, redistribution, or commercial exploitation is prohibited.
4. You retain rights in your User Content.
5. You grant us a worldwide, transferable license to host, reproduce, process, adapt, and use User Content as necessary to operate/improve/promote the Service (including model improvement where applicable), subject to law and Privacy Policy.
6. You are solely responsible for your User Content.

---

## 6. Payments and Refunds

1. Certain features are paid.
2. Prices may change with notice.
3. You authorize charges to provided payment method.
4. Subscriptions may auto-renew until canceled.
5. Trials may auto-convert unless canceled in time.
6. To the extent allowed by law, purchases are generally non-refundable unless required by law.

### EU Right of Withdrawal
EU users may withdraw within 14 days, unless digital-content exceptions apply after explicit prior consent to immediate performance and acknowledgment of withdrawal-right loss.

---

## 7. User Representations and Restrictions

By using the Service, you represent that:
- You have legal capacity and comply with applicable laws.
- You are at least 16.
- You do not use bots/scrapers/automated abusive tools.
- You do not misuse, disrupt, or circumvent security of the Service.
- You do not upload harmful, illegal, infringing, hateful, explicit, deceptive, or privacy-violating content.

We may suspend/terminate access for violations.

---

## 8. Disclaimer of Warranties

Service is provided **“AS IS”** and **“AS AVAILABLE”** without warranties (express or implied), to the fullest extent permitted by law.

---

## 9. Limitation of Liability

To the fullest extent permitted by law:
- We are not liable for indirect, incidental, consequential, special, exemplary, or punitive damages.
- Aggregate liability is limited to amounts paid by you to us for the Service.

---

## 10. Indemnification

You agree to indemnify and hold harmless the Company and related parties from claims/losses arising from your use of the Service, your User Content, or your breach of these Terms.

---

## 11. International Use

You are responsible for compliance with local laws in your jurisdiction.

---

## 12. Mandatory Arbitration and Class Action Waiver

1. Most disputes are resolved by final and binding arbitration.
2. You waive jury trial and class/representative action rights, where lawful.
3. Arbitration is administered by AAA under applicable rules.
4. Arbitration notice address (default): **Florinis, 7, Greg Tower, 2nd floor, 1065, Nicosia, Cyprus**.
5. Opt-out right: you may opt out of arbitration within 30 days of first use by written notice to {{SUPPORT_EMAIL}} (or arbitration notice address), with sufficient identifying details.

---

## 13. Governing Law

- Non-US users: laws of Cyprus (excluding conflict-of-laws principles), courts of Nicosia where court venue applies.
- US users: laws of the United States/Commonwealth of Virginia (as applicable in source terms), courts in Alexandria, Virginia where court venue applies.

---

## 14. Miscellaneous

- No waiver by delay.
- If one provision is unenforceable, remaining provisions survive.
- Terms are the entire agreement on subject matter.
- We may assign rights/obligations.
- Electronic notices/signatures are valid.
- Force majeure applies to events beyond reasonable control.

---

## 15. Contact

For legal notices/questions: **{{SUPPORT_EMAIL}}**.

---

**I HAVE READ THESE TERMS AND AGREE TO ALL OF THE PROVISIONS CONTAINED ABOVE.**
`;
